import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Avatar, Box, Button, CircularProgress, Stack, Typography, styled } from '@mui/material'
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'

import { capitalizeWords } from '@/utils/common'
import { Chip } from '@/components/UI/Chip'
import { Copy } from '@/components/UI/Copy'
import { ErrorPage } from '@/pages/Error'
import { useInstitutions } from '../useInstitutions'

// Rendered explicitly in the header or the fixed rows below, so they are not
// repeated in the additional-details section.
const HANDLED_FIELDS = [
  'id',
  'name',
  'status',
  'fullLogoUrl',
  'squareLogoUrl',
  'searchAliases',
]

// camelCase key -> readable label, so a field added by the service shows up
// with a sensible heading instead of needing a code change here.
const humaniseKey = (key: string) =>
  capitalizeWords(key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]/g, ' '))

const isScalar = (value: unknown): value is string | number | boolean =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'

// The response may carry fields beyond the typed ones. Render scalars and lists
// of scalars; skip anything more structured rather than printing
// "[object Object]".
const displayValue = (value: unknown): string | null => {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (isScalar(value)) return String(value)
  if (Array.isArray(value)) {
    const items = value.filter(isScalar).map(String)
    return items.length > 0 ? items.join(', ') : null
  }
  return null
}

// searchAliases is typed optional, but it arrives through an index signature
// of unknown, so narrow before use rather than trusting the declaration.
const readAliases = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) return undefined
  return value.filter((item): item is string => typeof item === 'string')
}

export const InstitutionDetail: React.FC = () => {
  const { institutionId } = useParams<{ institutionId: string }>()
  const navigate = useNavigate()
  const { data: institutions, isLoading, isError } = useInstitutions()

  const institution = useMemo(
    () => institutions?.find(item => item.id === institutionId),
    [institutions, institutionId]
  )

  // undefined = the service sent no such field; [] = it sent an empty list.
  // Those mean different things to an agent, so they render differently.
  const aliases = useMemo(
    () => (institution ? readAliases(institution.searchAliases) : undefined),
    [institution]
  )

  const additionalDetails = useMemo(() => {
    if (!institution) return []
    return Object.entries(institution)
      .filter(([key]) => !HANDLED_FIELDS.includes(key))
      .map(([key, value]) => [key, displayValue(value)] as const)
      .filter((entry): entry is readonly [string, string] => entry[1] !== null)
  }, [institution])

  const backToList = () => navigate('/institutions')

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={5}>
        <CircularProgress size={25} sx={{ color: 'lightgray' }} />
      </Box>
    )
  }

  if (isError) {
    return (
      <ErrorPage
        title={'Unable to Fetch Institution'}
        message={
          "We couldn't retrieve this institution at the moment. Please check your internet connection or try again later. If the issue persists, contact administrator."
        }
      />
    )
  }

  if (!institution) {
    return (
      <Box>
        <BackButton onClick={backToList} />
        <ErrorPage
          title={'Institution Not Found'}
          message={`No institution matches the ID ${institutionId}. It may have been removed from the supported list.`}
        />
      </Box>
    )
  }

  return (
    <Box>
      <BackButton onClick={backToList} />

      <Box p={2} sx={{ border: '1px solid #E4E7EC', borderRadius: '8px' }}>
        <Box
          sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}
          pb={2}
          mb={2}
          borderBottom="1px solid #EAECF0"
        >
          <Avatar
            sx={{ backgroundColor: '#F6F8F9', width: 40, height: 40 }}
            src={institution.squareLogoUrl}
          >
            <AccountBalanceOutlinedIcon sx={{ color: '#344054' }} />
          </Avatar>
          <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>{institution.name}</Typography>
          {institution.status && <Chip value={String(institution.status)} />}
        </Box>

        <Stack spacing={2}>
          <Detail label="Institution ID">
            <Copy
              value={institution.id}
              textStyle={{ fontSize: '14px', color: '#475467' }}
            />
          </Detail>

          <Detail label="Status">
            {institution.status ? (
              <Chip value={String(institution.status)} />
            ) : (
              <Value>N/A</Value>
            )}
          </Detail>

          {aliases && (
            <Detail label="Search Aliases">
              {aliases.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {aliases.map(alias => (
                    <Alias key={alias}>{alias}</Alias>
                  ))}
                </Box>
              ) : (
                <Value>None</Value>
              )}
            </Detail>
          )}

          {additionalDetails.map(([key, value]) => (
            <Detail key={key} label={humaniseKey(key)}>
              <Value>{value}</Value>
            </Detail>
          ))}
        </Stack>
      </Box>
    </Box>
  )
}

const BackButton = ({ onClick }: { onClick: () => void }) => (
  <Button
    onClick={onClick}
    startIcon={<ArrowBackIcon fontSize="small" />}
    sx={{
      textTransform: 'none',
      color: '#475467',
      fontSize: '14px',
      pl: 0,
      mb: 2,
      '&:hover': { backgroundColor: 'transparent', color: '#4C4CFF' },
    }}
  >
    Back to institutions
  </Button>
)

const Detail = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      alignItems: { xs: 'flex-start', sm: 'center' },
      gap: { xs: 0.5, sm: 2 },
    }}
  >
    <Label sx={{ minWidth: '160px' }}>{label}</Label>
    {children}
  </Box>
)

const Label = styled(Typography)({
  fontSize: '12px',
  fontWeight: 'bold',
  fontFamily: 'Inter',
  color: '#344054',
})

// Neutral pill, deliberately not the status Chip: an alias carries no state,
// so it should not borrow the status colour vocabulary.
const Alias = styled(Box)({
  fontSize: '13px',
  fontFamily: 'Inter',
  color: '#475467',
  backgroundColor: '#F6F8F9',
  border: '1px solid #E4E7EC',
  borderRadius: '9999px',
  padding: '2px 10px',
  width: 'fit-content',
})

const Value = styled(Typography)({
  fontSize: '14px',
  fontFamily: 'Inter',
  color: '#475467',
  wordBreak: 'break-word',
})
